import { useRef, useCallback } from "react";
import Hls from "hls.js";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 5000;
const WATCHDOG_TIMEOUT_MS = 5000;
const DEFAULT_ERROR = "RTMP feed not available due to server issue.";

export const useHlsPlayer = (
  videoRef,
  retryCount,
  setRetryCount,
  setErrorMessage,
  setIsLive,
  setIsLoading
) => {
  const hlsRef = useRef(null);
  const networkErrorTimeoutRef = useRef(null);
  const watchdogTimeoutRef = useRef(null);

  const clearWatchdog = useCallback(() => {
    if (watchdogTimeoutRef.current) {
      clearTimeout(watchdogTimeoutRef.current);
      watchdogTimeoutRef.current = null;
    }
  }, []);

  const cleanupPlayer = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (networkErrorTimeoutRef.current) {
      clearTimeout(networkErrorTimeoutRef.current);
      networkErrorTimeoutRef.current = null;
    }

    clearWatchdog();
  }, [clearWatchdog]);

  const handleNetworkErrorTimeout = useCallback(() => {
    cleanupPlayer();

    if (retryCount < MAX_RETRIES) {
      setRetryCount((prev) => prev + 1);
    } else {
      setErrorMessage(DEFAULT_ERROR);
      setIsLive(false);
      setIsLoading(false);
    }
  }, [retryCount, cleanupPlayer, setRetryCount, setErrorMessage, setIsLive, setIsLoading]);

  const setFatalErrorState = useCallback(() => {
    cleanupPlayer();
    setErrorMessage(DEFAULT_ERROR);
    setIsLive(false);
    setIsLoading(false);
  }, [cleanupPlayer, setErrorMessage, setIsLive, setIsLoading]);

  const handleFatalError = useCallback((data) => {
    switch (data.type) {
      case Hls.ErrorTypes.MEDIA_ERROR:
        if (data.details === "bufferAppendError") {
          setFatalErrorState();
        } else if (hlsRef.current) {
          hlsRef.current.recoverMediaError();
        }
        break;

      case Hls.ErrorTypes.NETWORK_ERROR:
        if (!networkErrorTimeoutRef.current) {
          networkErrorTimeoutRef.current = setTimeout(
            handleNetworkErrorTimeout,
            RETRY_DELAY_MS
          );
        }
        if (hlsRef.current) {
          hlsRef.current.startLoad();
        }
        break;

      default:
        setFatalErrorState();
        break;
    }
  }, [handleNetworkErrorTimeout, setFatalErrorState]);

  const initPlayer = useCallback((url) => {
    const video = videoRef.current;
    if (!video) {
      setErrorMessage("Video element not found.");
      setIsLoading(false);
      return;
    }

    if (!Hls.isSupported()) {
      setErrorMessage("HLS is not supported in this browser.");
      setIsLoading(false);
      return;
    }

    cleanupPlayer();

    hlsRef.current = new Hls();
    hlsRef.current.attachMedia(video);

    hlsRef.current.on(Hls.Events.MEDIA_ATTACHED, () => {
      if (!hlsRef.current) return;
      hlsRef.current.loadSource(url);

      clearWatchdog();
      watchdogTimeoutRef.current = setTimeout(
        () => {
          console.error("Stream timeout: no playback or error within 5s.");
          handleNetworkErrorTimeout();
        },
        WATCHDOG_TIMEOUT_MS
      );
    });

    hlsRef.current.on(Hls.Events.MANIFEST_PARSED, () => {
      video
        .play()
        .then(() => {
          clearWatchdog();
          setIsLoading(false);
          setIsLive(true);
          setRetryCount(0);

          if (networkErrorTimeoutRef.current) {
            clearTimeout(networkErrorTimeoutRef.current);
            networkErrorTimeoutRef.current = null;
          }
        })
        .catch((err) => {
          if (err.name === "AbortError") {
            return;
          }
          setIsLoading(false);
          setIsLive(false);
          setErrorMessage("Autoplay failed.");
        });
    });

    hlsRef.current.on(Hls.Events.ERROR, (event, data) => {
      clearWatchdog();
      if (data.fatal) {
        handleFatalError(data);
      }
    });
  }, [videoRef, cleanupPlayer, clearWatchdog, handleFatalError, setErrorMessage, setIsLoading, setIsLive, setRetryCount]);

  return { initPlayer, cleanupPlayer };
};
